import { useEffect, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Play, Trash } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const ROLE_GROUPS = [["role:project_manager", "All PMs"], ["role:lead", "All Leads"],
                     ["role:engineer", "All Engineers"], ["role:scm_manager", "SCM Managers"], ["role:admin", "Admins"]];

function freqLabel(c) {
  const hh = `${String(c.hour).padStart(2, "0")}:00 UTC`;
  if (c.frequency === "daily") return `Daily ${hh}`;
  if (c.frequency === "weekly") return `${WEEKDAYS[c.day] || "Mon"} ${hh}`;
  return `${c.day}${["st", "nd", "rd"][((c.day % 10) - 1)] || "th"} of month, ${hh}`;
}

export default function ScheduledReports({ isAdmin, users }) {
  const [configs, setConfigs] = useState([]);
  const [types, setTypes] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [running, setRunning] = useState("");
  const [form, setForm] = useState({ type: "weekly_status", frequency: "weekly", day: 0, hour: 8, recipients: ["role:project_manager"] });

  const load = async () => {
    try {
      const { data } = await api.get("/reports/configs");
      setConfigs(data.configs); setTypes(data.types);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/reports/configs", form);
      toast.success("Scheduled report created");
      setShowNew(false); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const toggleEnabled = async (c) => {
    try {
      await api.patch(`/reports/configs/${c.id}`, { enabled: !c.enabled });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const runNow = async (c) => {
    setRunning(c.id);
    try {
      const { data } = await api.post(`/reports/configs/${c.id}/run`, null, { timeout: 120000 });
      toast.success(`Report generated — filed as ${data.filename}, ${data.emails_sent} email(s) sent`);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setRunning(""); }
  };

  const del = async (c) => {
    try { await api.delete(`/reports/configs/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const recipLabel = (r) => {
    if (r.startsWith("role:")) return ROLE_GROUPS.find(([k]) => k === r)?.[1] || r;
    return users.find((u) => u.id === r)?.name || "user";
  };

  const toggleRecipient = (r) => setForm((f) => ({
    ...f, recipients: f.recipients.includes(r) ? f.recipients.filter((x) => x !== r) : [...f.recipients, r],
  }));

  return (
    <div className="space-y-2" data-testid="scheduled-reports">
      {configs.length === 0 && <div className="text-[11px] text-[var(--c-muted)]">No scheduled reports yet.</div>}
      {configs.map((c) => (
        <div key={c.id} className="flex items-center gap-3 py-1.5 border-b border-[var(--c-border)]/40 last:border-0"
          data-testid={`report-row-${c.id}`}>
          <div className="min-w-0">
            <div className="text-[12px] text-[var(--c-text)]">{types[c.type] || c.type}</div>
            <div className="text-[10px] text-[var(--c-muted)] font-mono">
              {freqLabel(c)} · {(c.recipients || []).map(recipLabel).join(", ") || "no recipients"} · Email + DMS + App
              {c.last_run_at && ` · last run ${c.last_run_at.slice(0, 16).replace("T", " ")}`}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-1.5 shrink-0">
            {isAdmin && (
              <>
                <button onClick={() => runNow(c)} disabled={running === c.id} data-testid={`report-run-${c.id}`} title="Generate now"
                  className="p-1.5 rounded-sm border border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-green)] hover:border-[var(--c-green)] transition-colors disabled:opacity-40">
                  <Play size={12} />
                </button>
                <button onClick={() => del(c)} data-testid={`report-del-${c.id}`} title="Delete"
                  className="p-1.5 rounded-sm border border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-red)] hover:border-[var(--c-red)] transition-colors">
                  <Trash size={12} />
                </button>
              </>
            )}
            <button onClick={() => isAdmin && toggleEnabled(c)} disabled={!isAdmin} data-testid={`report-toggle-${c.id}`}
              className={`h-5 px-2 rounded-full text-[9px] font-mono uppercase tracking-wider border transition-colors disabled:opacity-50 ${
                c.enabled ? "bg-[var(--c-green)]/15 border-[var(--c-green)]/50 text-[var(--c-green)]" : "bg-[var(--c-border)]/40 border-[var(--c-border)] text-[var(--c-muted)]"}`}>
              {c.enabled ? "on" : "off"}
            </button>
          </div>
        </div>
      ))}
      {isAdmin && (
        <Button size="sm" variant="outline" onClick={() => setShowNew(true)} data-testid="add-report-btn"
          className="h-7 text-xs border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] bg-transparent">
          <Plus size={12} className="mr-1" /> Add Report
        </Button>
      )}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)]" data-testid="report-dialog">
          <DialogHeader><DialogTitle className="text-[var(--c-text)] text-sm">New scheduled report</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[var(--c-muted)]">Report type</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="report-type" className="h-8 bg-[var(--c-bg)] border-[var(--c-border)] text-xs text-[var(--c-text)]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                  {Object.entries(types).map(([k, l]) => <SelectItem key={k} value={k} className="text-xs text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-[var(--c-muted)]">Frequency</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm((f) => ({ ...f, frequency: v }))}>
                  <SelectTrigger data-testid="report-freq" className="h-8 bg-[var(--c-bg)] border-[var(--c-border)] text-xs text-[var(--c-text)]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                    {["daily", "weekly", "monthly"].map((f) => <SelectItem key={f} value={f} className="text-xs text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[var(--c-muted)]">{form.frequency === "monthly" ? "Day of month" : form.frequency === "weekly" ? "Weekday" : "—"}</Label>
                {form.frequency === "weekly" ? (
                  <Select value={String(form.day)} onValueChange={(v) => setForm((f) => ({ ...f, day: Number(v) }))}>
                    <SelectTrigger data-testid="report-day" className="h-8 bg-[var(--c-bg)] border-[var(--c-border)] text-xs text-[var(--c-text)]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                      {WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)} className="text-xs text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <input type="number" min={1} max={28} value={form.day} disabled={form.frequency === "daily"}
                    onChange={(e) => setForm((f) => ({ ...f, day: Math.min(28, Math.max(1, Number(e.target.value) || 1)) }))}
                    data-testid="report-day-input"
                    className="w-full h-8 bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-2 text-xs text-[var(--c-text)] disabled:opacity-40" />
                )}
              </div>
              <div>
                <Label className="text-xs text-[var(--c-muted)]">Hour (UTC)</Label>
                <input type="number" min={0} max={23} value={form.hour}
                  onChange={(e) => setForm((f) => ({ ...f, hour: Math.min(23, Math.max(0, Number(e.target.value) || 0)) }))}
                  data-testid="report-hour"
                  className="w-full h-8 bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-2 text-xs text-[var(--c-text)]" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[var(--c-muted)]">Recipients</Label>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="report-recipients">
                {ROLE_GROUPS.map(([k, l]) => (
                  <button key={k} onClick={() => toggleRecipient(k)} data-testid={`report-rcpt-${k}`}
                    className={`h-6 px-2 rounded-full text-[10px] border transition-colors ${
                      form.recipients.includes(k) ? "bg-[var(--c-accent)]/15 border-[var(--c-accent)] text-[var(--c-text)]" : "border-[var(--c-border)] text-[var(--c-muted)]"}`}>
                    {l}
                  </button>
                ))}
                {users.map((u) => (
                  <button key={u.id} onClick={() => toggleRecipient(u.id)} data-testid={`report-rcpt-${u.id}`}
                    className={`h-6 px-2 rounded-full text-[10px] border transition-colors ${
                      form.recipients.includes(u.id) ? "bg-[var(--c-accent)]/15 border-[var(--c-accent)] text-[var(--c-text)]" : "border-[var(--c-border)] text-[var(--c-muted)]"}`}>
                    {u.name}
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={create} data-testid="report-create" className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent)]/85 text-white h-8 text-xs">
              Create scheduled report
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
